import { Flex } from "grid-styled";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLabels } from "pstat-anywhere/context_providers/LabelContext";
import { MIN_COMMENT_LENGTH } from "pstat-anywhere/utils/constants";
import { WarningBanner } from "pstat-design-system/banner";
import { TextareaFormElement } from "pstat-design-system/inputs/FormElements";
import Modal, { ModalFooter } from "pstat-design-system/modal";
import { RouterLink } from "pstat-design-system/RouterLink";
import { Button } from "pstat-design-system/shared/Button";
import { Text } from "pstat-design-system/text";

const RejectTemplateModal = ({ isOpen, onClose, onReject, isProcessing }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const labels = useLabels();
  const { documentLabel } = labels;

  const handleCommentChange = event => {
    setComment(event.target.value);
  };

  const isDisabled = comment.trim().length < MIN_COMMENT_LENGTH || isProcessing;

  const handleSubmit = event => {
    event.preventDefault();
    if (!isDisabled) {
      onReject(comment);
    }
  };

  const handleCancelKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      onClose();
    }
  };

  const handleRejectKeyDown = event => {
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const handleOnClose = () => {
    setComment("");
    onClose();
  };

  return (
    <Modal
      id="reject_template_modal"
      contentLabel={t(
        "systemDocumentTemplates.reviewTemplate.rejectModal.title",
        {
          documentLabel
        }
      )}
      isOpen={isOpen}
      minWidth={[320, 320, 400]}
      onRequestClose={handleOnClose}
      renderFooter={() => (
        <ModalFooter>
          <Flex width={1} px={5} py={3} justifyContent="space-between">
            <Button
              id="reject_template_button"
              buttonStyle="secondary"
              onClick={handleSubmit}
              onKeyDown={handleRejectKeyDown}
              processing={isProcessing}
              disabled={isDisabled}
            >
              {t(
                "systemDocumentTemplates.reviewTemplate.rejectModal.rejectButton"
              )}
            </Button>
            <Button
              buttonStyle="tertiary"
              onClick={handleOnClose}
              onKeyDown={handleCancelKeyDown}
            >
              {t("buttons.cancel")}
            </Button>
          </Flex>
        </ModalFooter>
      )}
    >
      <Flex
        flexDirection="column"
        px={5}
        pt={3}
        pb={5}
        width={["100%", "100%", 400]}
      >
        <WarningBanner mb={3} width={1}>
          <Text>
            {t(
              "systemDocumentTemplates.reviewTemplate.rejectModal.warningMessage.beforeLink"
            )}{" "}
            <RouterLink
              to="/policy_reports/expiring/template/rejected/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline" }}
            >
              {t(
                "systemDocumentTemplates.reviewTemplate.rejectModal.warningMessage.linkText"
              )}
            </RouterLink>
            .
          </Text>
        </WarningBanner>
        <form onSubmit={handleSubmit}>
          <TextareaFormElement
            id="reject_template_comment"
            label={t(
              "systemDocumentTemplates.reviewTemplate.rejectModal.commentLabel"
            )}
            value={comment}
            onChange={handleCommentChange}
            rows={4}
            required={true}
          />
        </form>
      </Flex>
    </Modal>
  );
};

export default RejectTemplateModal;
